import { useState, useEffect, useRef } from 'react';

/**
 * Owns session load-once-from-DB and clear-on-session-change/unmount logic.
 * Page supplies onRestoreState(savedState, persistenceSetters) and onClearState(persistenceSetters).
 * Returns persistence state and setters for the page to use.
 *
 * @param {Object} params
 * @param {string} params.sessionId
 * @param {Object|null} params.sessionLogger - SimpleSessionLogger instance
 * @param {string} [params.selectedClubId]
 * @param {string} [params.seasonId]
 * @param {Function} params.fetchPlayers
 * @param {Function} params.onRestoreState - (savedState, persistenceSetters) => void; persistenceSetters = { setStateLoadedFromDatabase, setIsLoadingState, setIsDataReady }
 * @param {Function} params.onClearState - (persistenceSetters) => void; called when sessionId changes or unmount
 */
export default function useCompetitiveSessionPersistence({
  sessionId,
  sessionLogger,
  selectedClubId,
  seasonId,
  fetchPlayers,
  onRestoreState,
  onClearState,
}) {
  const [stateLoadedFromDatabase, setStateLoadedFromDatabase] = useState(false);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);

  const hasAttemptedLoadRef = useRef(false);
  const currentSessionIdRef = useRef(sessionId);
  const componentMountTimeRef = useRef(Date.now());

  const persistenceSetters = {
    setStateLoadedFromDatabase,
    setIsLoadingState,
    setIsDataReady,
  };

  // Clear state when sessionId changes
  useEffect(() => {
    console.log('🔄 Dashboard: Session ID changed, clearing all state to prevent contamination');
    hasAttemptedLoadRef.current = false;
    currentSessionIdRef.current = sessionId;
    componentMountTimeRef.current = Date.now();
    onClearState(persistenceSetters);
    console.log('🔄 Dashboard: State cleared for new session:', sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Setters from useCourtManagement are stable; only run on sessionId change.
  }, [sessionId]);

  // Clear state on unmount
  useEffect(() => {
    return () => {
      console.log('🧹 Dashboard: Component unmounting, clearing all state from memory');
      onClearState(persistenceSetters);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: run cleanup on unmount only.
  }, []);

  // Load state from database (once per session when ready)
  useEffect(() => {
    if (currentSessionIdRef.current !== sessionId) {
      currentSessionIdRef.current = sessionId;
      hasAttemptedLoadRef.current = false;
      componentMountTimeRef.current = Date.now();
    }

    const timeSinceMount = Date.now() - componentMountTimeRef.current;
    if (timeSinceMount < 100 && hasAttemptedLoadRef.current) {
      console.log('🚨 Dashboard: Component remounted within 100ms, skipping state load to prevent corruption');
      return;
    }

    if (!sessionLogger || stateLoadedFromDatabase || hasAttemptedLoadRef.current || isLoadingState) {
      return;
    }

    if (!selectedClubId) {
      console.log('⏳ Dashboard: selectedClub not ready yet, waiting for club data...');
      return;
    }

    hasAttemptedLoadRef.current = true;

    const runLoad = async () => {
      try {
        setIsLoadingState(true);
        console.log('🔥 Dashboard: Starting session data load - player data first, then session state');

        if (!selectedClubId) {
          setStateLoadedFromDatabase(true);
          setIsLoadingState(false);
          return;
        }

        console.log('🔥 Dashboard: Step 1 - Loading player data with ELO ratings...');
        const playerResult = await fetchPlayers();
        if (!playerResult.success) {
          console.error('❌ Dashboard: Failed to load player data:', playerResult.error);
          setStateLoadedFromDatabase(true);
          setIsLoadingState(false);
          return;
        }
        console.log('✅ Dashboard: Player data loaded successfully with ELO ratings');

        console.log('🔥 Dashboard: Step 2 - Loading session state...');
        const savedState = await sessionLogger.loadCurrentState();
        console.log('🔥 Dashboard: Received savedState from database:', savedState);

        if (!savedState) {
          console.log('🔥 Dashboard: No saved state found - this is a new session, starting with default state');
          setStateLoadedFromDatabase(true);
          setIsLoadingState(false);
          setIsDataReady(true);
          return;
        }

        if (savedState.session_id !== sessionId) {
          console.error('🚨 CRITICAL: Loaded state belongs to different session!', {
            expected: sessionId,
            actual: savedState.session_id,
          });
          setStateLoadedFromDatabase(true);
          setIsLoadingState(false);
          return;
        }

        console.log('✅ Dashboard: Session ID validation passed for session:', sessionId);
        await onRestoreState(savedState, persistenceSetters);
      } catch (err) {
        console.error('❌ Failed to load state from database:', err);
        setStateLoadedFromDatabase(true);
        setIsLoadingState(false);
        setIsDataReady(false);
      }
    };

    runLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Intentional: run once when session/logger ready; full deps would cause loops.
  }, [sessionLogger, selectedClubId, seasonId]);

  return {
    stateLoadedFromDatabase,
    setStateLoadedFromDatabase,
    isLoadingState,
    setIsLoadingState,
    isDataReady,
    setIsDataReady,
  };
}
